import { useAtomValue } from "jotai";
import { useState } from "react";
import { toast } from "sonner";
import { inputClass } from "@/lib/forms";
import { CodeMatchBadge } from "@/modules/plus/photos/components/CodeMatchBadge";
import type { AccessoryPhoto } from "@/modules/plus/plus.schema";
import { atoms } from "@/state";

export const OrphanPhotosPage: React.FC = () => {
  const orphans = useAtomValue(atoms.photos.orphans);
  const isLoading = useAtomValue(atoms.photos.uploadedQuery).isLoading;

  return (
    <main className="mx-auto h-full max-w-5xl overflow-y-auto px-6 py-8">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Orphan photos</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Photos whose code doesn&apos;t attach to any model. Re-key them to a valid model code, or
          delete if they were mistakes.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading photos…</p>
      ) : orphans.length === 0 ? (
        <p className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300">
          No orphan photos. Everything attaches to a model.
        </p>
      ) : (
        <ol className="flex flex-col gap-6">
          {orphans.map((o) => (
            <OrphanCard key={o.code} code={o.code} photos={o.photos} />
          ))}
        </ol>
      )}
    </main>
  );
};

interface OrphanCardProps {
  code: string;
  photos: AccessoryPhoto[];
}

const OrphanCard: React.FC<OrphanCardProps> = ({ code, photos }) => {
  const [newCode, setNewCode] = useState("");
  const rename = useAtomValue(atoms.photos.renameOrphan);
  const del = useAtomValue(atoms.photos.deleteOrphan);

  const trimmed = newCode.trim();
  const canSave = trimmed !== "" && trimmed !== code && !rename.isPending;
  const isBusy = rename.isPending || del.isPending;

  const onSave = async () => {
    try {
      await rename.mutateAsync({ oldCode: code, newCode: trimmed });
      toast.success(`Moved to ${trimmed}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Rename failed");
    }
  };

  const onDelete = async () => {
    if (
      !window.confirm(
        `Delete ${photos.length} photo(s) uploaded under "${code}"? This can't be undone.`,
      )
    ) {
      return;
    }
    try {
      await del.mutateAsync(code);
      toast.success(`Deleted ${code}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  };

  return (
    <li className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="font-mono text-sm text-amber-900 dark:text-amber-200">{code}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {photos.length} photo{photos.length === 1 ? "" : "s"} • uploaded by{" "}
            {Array.from(new Set(photos.map((p) => p.uploaded_by))).join(", ")}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {photos.map((p) => (
          <a
            key={p.image_path}
            href={p.image_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block h-32 w-32 overflow-hidden rounded border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950"
            title={p.image_path?.split("/").pop() ?? p.image_path}
          >
            <img
              src={p.image_url}
              alt={code}
              className="h-full w-full object-contain"
              loading="lazy"
            />
          </a>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label
          className="text-xs font-medium text-gray-700 dark:text-gray-300"
          htmlFor={`newcode-${code}`}
        >
          New code:
        </label>
        <input
          id={`newcode-${code}`}
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          placeholder="e.g. 4360-RND-06-24x24"
          className={`${inputClass} min-w-[22rem] flex-1 font-mono`}
          disabled={isBusy}
        />
        <CodeMatchBadge code={trimmed} mismatchLabel="still orphan — no model matches" />
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={!canSave}
          onClick={onSave}
          className="rounded-md border border-blue-500 bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {rename.isPending ? "Saving…" : "Re-key photo"}
        </button>
        <button
          type="button"
          disabled={isBusy}
          onClick={onDelete}
          className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
        >
          {del.isPending ? "Deleting…" : "Delete photo(s)"}
        </button>
      </div>
    </li>
  );
};
